import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateFieldComponent } from '@clinic-platform/ui-components';

import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { ClientProfile } from '../data-access/client-profile.models';
import { ClientProfileService } from '../data-access/client-profile.service';

@Component({
  selector: 'app-client-profile-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    EmptyStateComponent,
    LoadingBlockComponent,
    TranslatePipe,
    DateFieldComponent,
    ReactiveFormsModule
  ],
  template: `
    <app-page-header
      title="My Profile"
      subtitle="Personal account information"
    />

    @if (isLoading) {
      <app-loading-block text="Loading profile..." />
    } @else if (errorText) {
      <app-empty-state
        title="Failed to load profile"
        [description]="errorText"
      />
    } @else if (!profile) {
      <app-empty-state
        title="Profile not found"
        description="No client profile is linked to your account yet."
      />
    } @else {
      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="profile-card">
        <div class="profile-card__photo-section">
          @if (profile.photoUrl) {
            <img
              [src]="profile.photoUrl"
              alt="Profile photo"
              class="profile-card__photo-image"
            />
          } @else {
            <div class="profile-card__photo-placeholder">No photo uploaded</div>
          }

          <div class="profile-card__photo-controls">
            <input
              type="file"
              accept="image/*"
              [disabled]="isPhotoUploading || isPhotoRemoving || isSaving"
              (change)="onPhotoSelected($event)"
            />

            @if (selectedPhotoName) {
              <div class="profile-card__meta-value">Selected: {{ selectedPhotoName }}</div>
            }

            <div class="profile-card__photo-actions">
              <button
                type="button"
                class="profile-card__secondary"
                [disabled]="!selectedPhotoFile || isPhotoUploading || isPhotoRemoving || isSaving"
                (click)="uploadPhoto()"
              >
                {{ isPhotoUploading ? 'Uploading...' : 'Upload photo' }}
              </button>

              @if (profile.photoUrl) {
                <button
                  type="button"
                  class="profile-card__secondary"
                  [disabled]="isPhotoUploading || isPhotoRemoving || isSaving"
                  (click)="removePhoto()"
                >
                  {{ isPhotoRemoving ? 'Removing...' : 'Remove photo' }}
                </button>
              }
            </div>
          </div>
        </div>

        <label class="profile-card__row">
          <span class="profile-card__label">Email</span>
          <div class="profile-card__value">{{ profile.email ?? '—' }}</div>
        </label>

        <label class="profile-card__row">
          <span class="profile-card__label">{{ 'Full Name' | t }}</span>
          <input class="profile-card__input" type="text" formControlName="fullName" />
        </label>
        <label class="profile-card__row">
          <span class="profile-card__label">{{ 'Phone' | t }}</span>
          <input class="profile-card__input" type="text" formControlName="phone" />
        </label>
        <label class="profile-card__row">
          <span class="profile-card__label">{{ 'Birth Date' | t }}</span>
          <lib-date-field inputId="client-profile-birthDate" formControlName="birthDate" />
        </label>
        <label class="profile-card__row">
          <span class="profile-card__label">Current Password</span>
          <input class="profile-card__input" type="password" formControlName="oldPassword" />
        </label>

        <label class="profile-card__row">
          <span class="profile-card__label">New Password</span>
          <input class="profile-card__input" type="password" formControlName="newPassword" />
        </label>

        @if (actionErrorText) {
          <div class="profile-card__error">
            {{ actionErrorText | t }}
          </div>
        }

        @if (successText) {
          <div class="profile-card__success">
            {{ successText }}
          </div>
        }

        <button type="submit" class="profile-card__submit" [disabled]="isSaving || form.invalid">
          {{ isSaving ? 'Saving...' : 'Save profile' }}
        </button>
      </form>
    }
  `,
  styles: [`
    .profile-card {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px;
      display: grid;
      gap: 12px;
      max-width: 640px;
    }

    .profile-card__photo-section {
      display: grid;
      grid-template-columns: 112px 1fr;
      gap: 16px;
      align-items: start;
      padding-bottom: 12px;
      border-bottom: 1px solid #f3f4f6;
    }

    .profile-card__photo-image,
    .profile-card__photo-placeholder {
      width: 112px;
      height: 112px;
      border-radius: 16px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
    }

    .profile-card__photo-image {
      object-fit: cover;
    }

    .profile-card__photo-placeholder {
      display: grid;
      place-items: center;
      padding: 12px;
      color: #6b7280;
      font-size: 12px;
      text-align: center;
    }

    .profile-card__photo-controls {
      display: grid;
      gap: 8px;
    }

    .profile-card__photo-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .profile-card__meta-value {
      color: #111827;
      font-size: 14px;
      word-break: break-word;
    }

    .profile-card__row {
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 12px;
      align-items: baseline;
      padding-bottom: 10px;
      border-bottom: 1px solid #f3f4f6;
    }

    .profile-card__row:last-child {
      padding-bottom: 0;
      border-bottom: none;
    }

    .profile-card__label {
      color: #6b7280;
      font-size: 13px;
      font-weight: 600;
    }

    .profile-card__value {
      color: #111827;
      font-size: 14px;
      word-break: break-word;
    }

    .profile-card__input {
      width: 100%;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      box-sizing: border-box;
      font: inherit;
      color: #111827;
      background: #ffffff;
    }

    .profile-card__error {
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 14px;
    }

    .profile-card__success {
      padding: 10px 12px;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      background: #f0fdf4;
      color: #166534;
      font-size: 14px;
    }

    .profile-card__submit {
      justify-self: start;
      height: 36px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 14px;
      cursor: pointer;
    }

    .profile-card__secondary {
      height: 36px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #ffffff;
      color: #111827;
      padding: 0 14px;
      cursor: pointer;
    }

    .profile-card__secondary:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .profile-card__submit:disabled {
      opacity: 0.6;
      cursor: default;
    }

    @media (max-width: 768px) {
      .profile-card__photo-section,
      .profile-card__meta-grid,
      .profile-card__row {
        grid-template-columns: 1fr;
        gap: 4px;
      }
    }
  `]
})
export class ClientProfilePageComponent implements OnInit
{
  private readonly clientProfileService = inject(ClientProfileService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly form = this.formBuilder.group({
    fullName: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(255)]),
    phone: this.formBuilder.nonNullable.control('', [Validators.maxLength(50)]),
    birthDate: this.formBuilder.nonNullable.control(''),
    oldPassword: this.formBuilder.nonNullable.control('', [Validators.minLength(8), Validators.maxLength(255)]),
    newPassword: this.formBuilder.nonNullable.control('', [Validators.minLength(8), Validators.maxLength(255)])
  });

  protected profile: ClientProfile | null = null;
  protected isLoading = true;
  protected isSaving = false;
  protected isPhotoUploading = false;
  protected isPhotoRemoving = false;
  protected selectedPhotoFile: File | null = null;
  protected selectedPhotoName = '';
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected successText: string | null = null;

  public ngOnInit(): void
  {
    this.isLoading = true;
    this.errorText = null;

    this.clientProfileService.getMyProfile().subscribe({
      next: (profile: ClientProfile | null) =>
      {
        this.profile = profile;
        this.form.patchValue({
          fullName: profile?.fullName ?? '',
          phone: profile?.phone ?? '',
          birthDate: this.normalizeBirthDateForInput(profile?.birthDate ?? null),
          oldPassword: '',
          newPassword: ''
        });
        this.isLoading = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load client profile', error);
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load profile'
        });
        this.isLoading = false;
      }
    });
  }

  protected onSubmit(): void
  {
    if (!this.profile || this.form.invalid || this.isSaving)
    {
      return;
    }

    const value = this.form.getRawValue();
    const oldPassword = value.oldPassword.trim();
    const newPassword = value.newPassword.trim();

    if ((oldPassword && !newPassword) || (!oldPassword && newPassword))
    {
      this.actionErrorText = 'Provide both current and new password to change password.';
      return;
    }

    this.isSaving = true;
    this.actionErrorText = null;
    this.successText = null;

    this.clientProfileService.updateMyProfile({
      fullName: value.fullName.trim() || this.profile.fullName,
      phone: value.phone.trim(),
      birthDate: value.birthDate.trim() ? value.birthDate.trim() : null,
      oldPassword: oldPassword || undefined,
      newPassword: newPassword || undefined
    }).subscribe({
      next: (updated) =>
      {
        this.profile = updated;
        this.form.patchValue({
          oldPassword: '',
          newPassword: ''
        });
        this.successText = 'Profile updated.';
        this.isSaving = false;
      },
      error: (error: unknown) =>
      {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic'
        });
        this.isSaving = false;
      }
    });
  }

  protected onPhotoSelected(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0] ?? null;
    this.selectedPhotoFile = file;
    this.selectedPhotoName = file?.name ?? '';
    if (target) {
      target.value = '';
    }
  }

  protected uploadPhoto(): void {
    if (!this.profile || !this.selectedPhotoFile || this.isPhotoUploading || this.isPhotoRemoving || this.isSaving) {
      return;
    }

    this.isPhotoUploading = true;
    this.actionErrorText = null;
    this.successText = null;

    this.clientProfileService.uploadMyPhoto(this.selectedPhotoFile).subscribe({
      next: (updated) => {
        this.profile = updated;
        this.selectedPhotoFile = null;
        this.selectedPhotoName = '';
        this.isPhotoUploading = false;
        this.successText = 'Profile photo uploaded.';
      },
      error: (error: unknown) => {
        this.isPhotoUploading = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  protected removePhoto(): void {
    if (!this.profile || !this.profile.photoUrl || this.isPhotoUploading || this.isPhotoRemoving || this.isSaving) {
      return;
    }

    this.isPhotoRemoving = true;
    this.actionErrorText = null;
    this.successText = null;

    this.clientProfileService.removeMyPhoto().subscribe({
      next: (updated) => {
        this.profile = updated;
        this.selectedPhotoFile = null;
        this.selectedPhotoName = '';
        this.isPhotoRemoving = false;
        this.successText = 'Profile photo removed.';
      },
      error: (error: unknown) => {
        this.isPhotoRemoving = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  private normalizeBirthDateForInput(value: string | null): string
  {
    if (!value) {
      return '';
    }

    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const year = parsed.getUTCFullYear();
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
